/**
 * What each filter surface can do, and how its request is built. [IT-707 P2.1]
 *
 * Before this module, four payload builders lived in three stores and four views,
 * each renaming keys on the way out. That rename-in-transit layer is where both
 * IT-703 bugs lived: `groupId` vs `rgidSearchFreeForm`, and `fromDateVal` vs
 * `fromDt`. One place to rename is one place to get it wrong.
 *
 * Two layers, deliberately separate (conflating them is how you get two controls
 * bound to one piece of state):
 *
 *   CAPABILITY is a property of the BACKEND SURFACE — can this criterion reach the
 *   endpoint and narrow the result? It decides what is sent and, from P2.2, what is
 *   rendered.
 *
 *   PLACEMENT is a property of the VIEW — modal, or page-level quick toggle. It
 *   lives with the views, not here.
 *
 * P2.1 is a pure refactor: every request is byte-identical to what the old
 * builders produced, pinned by filter-request-characterization.spec.js. The
 * per-surface serialisation differences below look untidy because they ARE
 * untidy — they are preserved exactly, and normalising them is P2.2's job.
 */

export const SURFACES = {
  PHOTO_TAGGING: 'photoTagging',
  PHOTO_QC: 'photoQc',
  GROUP_TAGGING: 'groupTagging',
  GROUP_QC: 'groupQc',
}

/** Every criterion the filter UI can collect, in filter-side vocabulary. */
export const CRITERIA = [
  'fromDt', 'thruDt', 'tags', 'projects', 'jobs', 'taggers',
  'respIdSearchFreeForm', 'rgidSearchFreeForm',
  'onlyUntagged', 'includePartiallyTagged', 'includeInactive',
  'onlyNeedsReview', 'showQCed',
  'country', 'retailer', 'department', 'question_id',
]

/**
 * Criteria each surface cannot apply, with the reason.
 *
 * Every entry is a claim that a control would do nothing on that page. From P2.2
 * these criteria stop rendering; until then they render and are ignored, which is
 * the status quo this refactor must not change.
 */
const UNSUPPORTED = {
  [SURFACES.PHOTO_TAGGING]: {
    taggers: 'A checkout queue has no tagger assigned yet.',
    onlyNeedsReview: 'Review state is meaningless before tagging.',
    showQCed: 'Review state is meaningless before tagging.',
  },
  [SURFACES.PHOTO_QC]: {
    onlyUntagged: 'QC pins tagged=True.',
    includePartiallyTagged: 'QC pins tagged=True.',
  },
  [SURFACES.GROUP_TAGGING]: {
    tags: 'get_groups serves untagged_groups(), so the queue has no tags to filter.',
    taggers: 'A checkout queue has no tagger assigned yet.',
    onlyUntagged: 'get_groups hardcodes untagged_groups() — permanently true.',
    includePartiallyTagged: 'Partial tagging is per-photo; PhotoGroup has no analogue.',
    onlyNeedsReview: 'Review state is meaningless before tagging.',
    showQCed: 'Review state is meaningless before tagging.',
  },
  [SURFACES.GROUP_QC]: {
    onlyUntagged: 'QC pins tagged=True.',
    includePartiallyTagged: 'Partial tagging is per-photo; PhotoGroup has no analogue.',
  },
}

/** Criteria a surface sends. Derived, so a criterion is never silently absent. */
export function supportedCriteria(surface) {
  const blocked = UNSUPPORTED[surface] || {}
  return CRITERIA.filter((key) => !(key in blocked))
}

/**
 * The raw keys a surface excludes, NOT filtered through CRITERIA.
 *
 * `supportedCriteria` derives from CRITERIA, so a typo'd exclusion key would
 * quietly fail to exclude anything and never show up there. This exposes the
 * literal keys so a ghost can be asserted against.
 */
export function excludedKeys(surface) {
  return Object.keys(UNSUPPORTED[surface] || {})
}

export function unsupportedReason(surface, criterion) {
  return (UNSUPPORTED[surface] || {})[criterion] || null
}

export function supports(surface, criterion) {
  return !(criterion in (UNSUPPORTED[surface] || {}))
}

/* ------------------------------------------------------------------ */
/* Value helpers                                                       */
/* ------------------------------------------------------------------ */

/** `[{id: 1}] -> [1]`. Photo surfaces assume objects and would emit undefined. */
const strictIds = (list) => (list || []).map((i) => i.id)

/** `[{id: 1}] -> [1]`, `[1] -> [1]`. Group surfaces tolerate pre-mapped ids. */
const looseIds = (list) => (list || []).map((i) => i.id || i)

/* ------------------------------------------------------------------ */
/* Per-surface serialisation                                           */
/* ------------------------------------------------------------------ */

const SERIALISERS = {
  [SURFACES.PHOTO_TAGGING]: (f) => ({
    fromDateVal: f.fromDt,
    toDateVal: f.thruDt,
    onlyUntagged: f.onlyUntagged,
    // Defaults TRUE when absent, not false — an omitted key must not silently
    // narrow the queue to fully-untagged photos.
    includePartiallyTagged: f.includePartiallyTagged !== undefined ? f.includePartiallyTagged : true,
    includeInactive: f.includeInactive === true,
    tags: strictIds(f.tags),
    jobs: strictIds(f.jobs),
    projects: strictIds(f.projects),
    respIdSearchFreeForm: f.respIdSearchFreeForm,
    rgidSearchFreeForm: f.rgidSearchFreeForm,
    // IT-791 added this to every chain by hand while P2.1 was in flight. It has
    // to be here too, or resolving that merge toward this builder would silently
    // re-drop it — the exact class IT-707 exists to remove.
    question_id: f.question_id,
    country: f.country,
    retailer: f.retailer,
    department: f.department,
  }),

  [SURFACES.PHOTO_QC]: (f) => ({
    fromDateVal: f.fromDt,
    toDateVal: f.thruDt,
    tags: strictIds(f.tags),
    jobs: strictIds(f.jobs),
    projects: strictIds(f.projects),
    taggers: strictIds(f.taggers),
    respIdSearchFreeForm: f.respIdSearchFreeForm,
    // IT-707 P2.4: convert_qc_query_params_to_filters has parsed this since
    // before the audit; the UI simply never sent it. Closing a gap, not adding
    // a backend capability.
    rgidSearchFreeForm: f.rgidSearchFreeForm,
    question_id: f.question_id,  // IT-791
    onlyNeedsReview: f.onlyNeedsReview,
    showQCed: f.showQCed,
    includeInactive: f.includeInactive === true,
    country: f.country,
    retailer: f.retailer,
    department: f.department,
  }),

  [SURFACES.GROUP_QC]: (f) => ({
    onlyNeedsReview: f.onlyNeedsReview === true,
    showQCed: f.showQCed === true,
    // Coerced like tags/projects/jobs on this surface. It used to pass through
    // verbatim, which was invisible while every caller pre-mapped ids — and
    // stopped being safe the moment boxes.js fetchQcBatch started using this
    // builder, since it hands over a raw PhotoFilter payload. looseIds is a
    // no-op on ids, so no existing caller's request changes.
    taggers: looseIds(f.taggers),
    question_id: f.question_id,
    retailer: f.retailer,
    department: f.department,
    country: f.country,
    fromDateVal: f.fromDt,
    toDateVal: f.thruDt,
    respIdSearchFreeForm: f.respIdSearchFreeForm,
    // IT-703: `groupId` is the pre-rename emitter key. Kept as a guard against a
    // partial revert or bad merge reintroducing it — not for stale tabs, which
    // cannot produce a mixed state (one bundle, no lazy chunks).
    rgidSearchFreeForm: f.rgidSearchFreeForm ?? f.groupId,
    projects: looseIds(f.projects),
    jobs: looseIds(f.jobs),
    tags: looseIds(f.tags),
    // IT-708: honoured by apply_group_filters since P2.3 — it widens the
    // correlated photo subquery to Photo.objects_all.
    includeInactive: f.includeInactive === true,
  }),
}

/**
 * The request body for a POST surface.
 *
 * Cleared criteria stay present as `null`. The backend gates on `if value:`, so
 * null and absent are equivalent to it — but changing which one we send would be
 * a diff, and P2.1 promises none.
 */
export function buildPayload(surface, filter) {
  const serialise = SERIALISERS[surface]
  if (!serialise) throw new Error(`unknown filter surface: ${surface}`)
  return serialise(filter || {})
}

/**
 * The query string for group tagging, which is a GET.
 *
 * Unlike the POST surfaces this OMITS falsy values rather than sending them as
 * null, and repeats `projects`/`jobs` once per id. Append order is fixed because
 * it is part of the characterised output.
 */
export function buildQueryString(filter) {
  const f = filter || {}
  const params = new URLSearchParams()

  const single = [
    ['question_id', f.question_id],
    ['retailer', f.retailer],
    ['department', f.department],
    ['country', f.country],
    ['fromDateVal', f.fromDt],
    ['toDateVal', f.thruDt],
    ['respIdSearchFreeForm', f.respIdSearchFreeForm],
    ['rgidSearchFreeForm', f.rgidSearchFreeForm],
    // IT-708. Falsy is omitted below, which is what the backend wants: absent
    // and false mean the same to it, and sending the string 'false' on a GET is
    // the trap `_is_truthy` exists to absorb.
    ['includeInactive', f.includeInactive === true ? 'true' : null],
  ]
  single.forEach(([key, value]) => {
    if (value) params.append(key, value)
  })

  looseIds(f.projects).forEach((id) => params.append('projects', id))
  looseIds(f.jobs).forEach((id) => params.append('jobs', id))

  return params.toString()
}
